import React, { useState, useEffect } from 'react';
import { api } from '../api';
import '../App.css';

function Reports() {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      const data = await api.getCampaigns();
      setCampaigns(data);
      setLoading(false);
    } catch (error) {
      alert('Failed to load campaigns');
      setLoading(false);
    }
  };

  const loadResults = async (campaignId) => {
    try {
      const data = await api.getCampaignResults(campaignId);
      setResults(data);
    } catch (error) {
      alert('Failed to load results');
    }
  };

  const handleCampaignChange = (campaignId) => {
    setSelectedCampaign(campaignId);
    if (!campaignId) {
      setResults([]);
      return;
    }
    loadResults(campaignId);
  };

  const handleExport = async () => {
    if (!selectedCampaign) {
      alert("Please select a campaign first");
      return;
    }

    try {
      const blob = await api.exportCampaignResults(selectedCampaign);
      if (blob.size === 0) {
        alert("No calls to export yet");
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `campaign_${selectedCampaign}_results.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      alert('Export failed: ' + error.message);
    }
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  const campaign = campaigns.find(c => c.id === selectedCampaign);

  return (
    <div>
      <h1 style={{ fontSize: '2rem', fontWeight: 600, marginBottom: '1.5rem' }}>Reports</h1>

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 className="card-title">Campaign Results</h3>
            <p className="card-description">View and export call results</p>
          </div>
          <button
            className="btn btn-primary"
            onClick={handleExport}
            disabled={!selectedCampaign}
            data-testid="btn-export-results"
          >
            Export CSV
          </button>
        </div>

        <div className="form-group">
          <label className="form-label">Select Campaign</label>
          <select
            className="form-select"
            value={selectedCampaign || ''}
            onChange={(e) => handleCampaignChange(e.target.value)}
            data-testid="select-campaign"
          >
            <option value="">-- Select Campaign --</option>
            {campaigns.map(c => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.status})
              </option>
            ))}
          </select>
        </div>

        {selectedCampaign && (
          <div>
            <div className="stats-container" style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
              <div className="stat-card">
                <h4 className="stat-label">Total Leads</h4>
                <p className="stat-value">{campaign?.stats?.total_leads || 0}</p>
              </div>
              <div className="stat-card">
                <h4 className="stat-label">Dialed</h4>
                <p className="stat-value">{campaign?.stats?.dialed || 0}</p>
              </div>
              <div className="stat-card">
                <h4 className="stat-label">No Answer</h4>
                <p className="stat-value">{campaign?.stats?.no_answer || 0}</p>
              </div>
              <div className="stat-card">
                <h4 className="stat-label">Live/AM</h4>
                <p className="stat-value">{campaign?.stats?.live_or_am || 0}</p>
              </div>
              <div className="stat-card">
                <h4 className="stat-label">Busy</h4>
                <p className="stat-value">{campaign?.stats?.busy || 0}</p>
              </div>
              <div className="stat-card">
                <h4 className="stat-label">Line Error</h4>
                <p className="stat-value">{campaign?.stats?.line_error || 0}</p>
              </div>
              <div className="stat-card">
                <h4 className="stat-label">Transferred</h4>
                <p className="stat-value">{campaign?.stats?.transferred || 0}</p>
              </div>
              <div className="stat-card">
                <h4 className="stat-label" style={{color:'#8b5cf6'}}>Voicemail</h4>
                <p className="stat-value" style={{color:'#8b5cf6'}}>{campaign?.stats?.voicemail || 0}</p>
              </div>
            </div>

            <div className="table-container">
              <table className="data-table" data-testid="results-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Address</th>
                    <th>City</th>
                    <th>State</th>
                    <th>Status</th>
                    <th>Disposition</th>
                    <th>Attempts</th>
                    <th>Transferred</th>
                    <th>Last Call</th>
                  </tr>
                </thead>
                <tbody>
                  {results.length === 0 ? (
                    <tr>
                      <td colSpan="10" style={{ textAlign: 'center', padding: '2rem' }}>
                        No results yet
                      </td>
                    </tr>
                  ) : (
                    results.map((lead) => (
                      <tr key={lead.id} data-testid="result-row">
                        <td style={{ fontWeight: 500 }}>
                          {lead.first_name || lead.last_name 
                            ? `${lead.first_name || ''} ${lead.last_name || ''}`.trim() 
                            : '-'}
                        </td>
                        <td>{lead.phone_number}</td>
                        <td>{lead.address || '-'}</td>
                        <td>{lead.city || '-'}</td>
                        <td>{lead.state || '-'}</td>
                        <td>
                          <span className={`badge badge-${lead.status === 'completed' ? 'success' : lead.status === 'dialing' ? 'warning' : 'secondary'}`}>
                            {lead.status}
                          </span>
                        </td>
                        <td>{lead.disposition || '-'}</td>
                        <td>{lead.call_attempts}</td>
                        <td>{lead.transferred ? 'Yes' : 'No'}</td>
                        <td>{lead.last_call_time ? new Date(lead.last_call_time).toLocaleString() : '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Reports;
